import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { RADIO_CHANNELS, uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Radio Panel - Quick message overlay
 *
 * Appears above the chat box, or above its bubble when minimized, when
 * Z/X/C is pressed. Shows numbered preset messages for the active channel.
 * Press 1-9/0 to send, ESC or same key to close.
 *
 * @element radio-panel
 */
@customElement('radio-panel')
export class RadioPanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .radio-box {
        /* Opts in itself: above the minimized bubble there's no chat
           wrapper to inherit it from. */
        pointer-events: auto;
        background-color: var(--color-bg-panel);
        border-radius: var(--radius-md);
        padding: 10px 0;
        margin-bottom: 6px;
        margin-left: 58px;
        font-size: var(--font-size-13);
        color: var(--color-text-primary);
      }

      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px 6px;
        border-bottom: 1px solid var(--color-border-light);
        margin-bottom: 4px;
      }

      .channel-name {
        color: var(--color-players-active);
        font-weight: 600;
        font-size: var(--font-size-13);
      }

      .customize-link {
        color: var(--color-text-secondary);
        font-size: var(--font-size-xs);
        cursor: pointer;
        text-decoration: underline;
        transition: color 0.15s;
      }

      .close-hint {
        padding: 4px 12px 6px;
        color: var(--color-text-secondary);
        font-size: var(--font-size-xs);
        border-bottom: 1px solid var(--color-border-light);
        margin-bottom: 2px;
      }

      .message-row {
        display: flex;
        align-items: flex-start;
        padding: 5px 12px;
        cursor: pointer;
        transition: background-color 0.1s;
      }

      .message-index {
        color: var(--color-text-secondary);
        min-width: 24px;
        font-size: var(--font-size-sm);
        flex-shrink: 0;
      }

      .message-text {
        color: var(--color-text-primary);
        font-size: var(--font-size-13);
        word-break: break-word;
      }

      @media (hover: hover) {
        .customize-link:hover {
          color: var(--color-text-white);
        }

        .message-row:hover {
          background-color: var(--color-overlay-subtle);
        }
      }
    `,
  ];

  private state = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  override render() {
    const radio = this.state.radio;
    if (!radio) return nothing;
    const channel = RADIO_CHANNELS[radio.activeChannel];

    return html`
      <div class="radio-box">
        <div class="header">
          <span class="channel-name">${channel.name}</span>
          <span class="customize-link" @click=${this.handleCustomize}>Customize</span>
        </div>
        <div class="close-hint">Close - Press ${channel.key} or ESC</div>
        ${radio.messages.slice(0, 10).map(
          (msg, i) => html`
            <div class="message-row" data-index=${i} @click=${this.handleRowClick}>
              <span class="message-index">${(i + 1) % 10}.</span>
              <span class="message-text">${msg}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private handleRowClick(e: Event): void {
    e.stopPropagation();
    if (!(e.currentTarget instanceof HTMLElement)) return;
    this.api.playClick();
    this.api.sendRadioMessage(Number(e.currentTarget.dataset['index']));
  }

  private handleCustomize(e: Event): void {
    e.stopPropagation();
    const radio = this.state.radio;
    if (!radio) return;
    const channel = radio.activeChannel;
    this.api.playClick();
    this.api.hideRadio();
    this.api.showRadioConfig(channel);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'radio-panel': RadioPanel;
  }
}
